import React, { Component, MouseEvent, ReactNode } from "react";
import { observer } from "mobx-react";

import { combineFilters } from "@formant/core/common/combineFilters";
import { defined } from "@formant/core/common/defined";
import { withoutReservedTags } from "@formant/core/common/withoutReservedTags";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { IEvent } from "@formant/core/model/IEvent";
import { IFilter } from "@formant/core/model/IFilter";
import { StreamType } from "@formant/core/model/StreamType";
import { Icon } from "@formant/ui/component/common/Icon";

import {
    annotationStore,
    annotationTemplateStore,
    appStore,
    authStore,
    commandStore,
    commandTemplateStore,
    commentStore,
    deviceStore,
    observeStore,
    organizationStore,
} from "../../stores";
import { formatDateTime } from "../../utils/formatDateTime";
import { formatUserIdDisplayName } from "../../utils/formatUserDisplayName";
import { Avatar } from "../common/Avatar";
import { EventChip } from "../common/EventChip";
import { EventHeadline } from "../common/EventHeadline";
import { EventIcon } from "../common/EventIcon";
import { Heading2 } from "../common/Heading2";
import { IconButton } from "../common/IconButton";
import { Linkify } from "../common/Linkify";
import { Scrollable } from "../common/Scrollable";
import { StandardButton } from "../common/StandardButton";
import { StreamIcon } from "../common/StreamIcon";
import { TextButton } from "../common/TextButton";
import * as styles from "./EventDetail.scss";

export type SaveMode = "save" | "save-close" | "save-new";

export interface IEventDetailProps {
    event: IEvent;
    context: IFilter;
    filter?: IFilter;
    title: string;
}

@observer
export class EventDetail extends Component<IEventDetailProps> {
    public render() {
        const { onClose, onBackToList, onEdit, onDelete, onExportTelemetry } =
            this;
        const { event, title } = this.props;
        const {
            time,
            endTime,
            message,
            deviceId,
            streamName,
            streamType,
            tags,
            type,
        } = event;

        const userId = (event as any).userId; // TODO lift userId field to IBaseEvent

        const mutable =
            event.type === "annotation" ||
            (event.type === "comment" && event.userId === authStore.userId);
        const exportable =
            organizationStore.dataExportEnabled() && endTime && time < endTime;

        const details: [string, ReactNode][] = [];
        if (!endTime) {
            details.push(["Time", formatDateTime(parseIsoDate(time))]);
        } else {
            details.push(["Start time", formatDateTime(parseIsoDate(time))]);
            details.push(["End time", formatDateTime(parseIsoDate(endTime))]);
        }

        details.push([
            "On-Demand",
            <StandardButton key="on_demand" onClick={this.onDownload}>
                Retrieve
            </StandardButton>,
        ]);

        if (event.type === "comment" && event.editedAt) {
            details.push([
                "Edited time",
                formatDateTime(parseIsoDate(event.editedAt)),
            ]);
        }
        if (deviceId) {
            const device = deviceStore.get(deviceId);
            const deviceName = device && device.name;
            if (deviceName) {
                details.push(["Device", deviceName]);
            }
        }
        if (streamName) {
            details.push(["Stream name", streamName]);
        }
        if (streamType) {
            details.push([
                "Stream type",
                <>
                    <StreamIcon type={streamType} />
                    {streamType}
                </>,
            ]);
        }
        if (tags) {
            Object.keys(withoutReservedTags(tags))
                .sort()
                .forEach(tagKey => {
                    details.push([
                        tagKey,
                        <>
                            <Icon name="tags" />
                            {tags[tagKey] || ""}
                        </>,
                    ]);
                });
        }
        if (message) {
            details.push(["Message", <Linkify text={message} />]);
        }
        if (event.type === "annotation") {
            const { note, taggedUsers, annotationTemplateId } = event;
            if (note) {
                details.push(["Note", <Linkify text={note} />]);
            }
            if (taggedUsers) {
                Object.keys(taggedUsers)
                    .sort()
                    .forEach(taggedUserKey => {
                        const users = taggedUsers[taggedUserKey]
                            .flatMap(_ => {
                                const name = formatUserIdDisplayName(_);
                                return (name && [name]) || [];
                            })
                            .join(", ");
                        details.push([
                            taggedUserKey,
                            <>
                                <Icon name="user" />
                                {users}
                            </>,
                        ]);
                    });
            }

            const annotationTemplate =
                annotationTemplateId &&
                annotationTemplateStore.get(annotationTemplateId);
            if (annotationTemplate) {
                details.push(["Annotation", annotationTemplate.name]);
            }
        } else if (event.type === "task-summary") {
            const summary = event.report;
            Object.keys(summary).forEach(summaryKey => {
                details.push([
                    summaryKey,
                    JSON.stringify(summary[summaryKey], null, 2),
                ]);
            });
        } else if (
            event.type === "datapoint-event" ||
            event.type === "triggered-event" ||
            event.type === "device-offline" ||
            event.type === "device-online"
        ) {
            details.push(["Severity", event.severity]);
        } else if (event.type === "custom" && event.severity) {
            details.push(["Severity", event.severity]);
        } else if (event.type === "stateful") {
            if (event.severity) {
                details.push(["Severity", event.severity]);
            }
            details.push(["Stateful Event ID", event.id]);
            if (!event.endTime) {
                details.push(["Stateful Event Status", "Active"]);
            } else {
                details.push(["Stateful Event Status", "Cleared"]);
            }
        } else if (
            event.type === "command-request" ||
            event.type === "command-response" ||
            event.type === "command-delivery"
        ) {
            const command = commandStore.get(event.commandId);
            if (command && command.commandTemplateId) {
                const template = commandTemplateStore.get(
                    command.commandTemplateId,
                );
                if (template) {
                    details.push(["Command", template.name]);
                }
            }
        }
        details.push(["Type", type]);
        return (
            <div className={styles["event-detail"]}>
                <Heading2 className={styles.heading}>
                    <EventChip event={event} />
                    <EventIcon event={event} className={styles["event-icon"]} />
                    <TextButton
                        className={styles.title}
                        onClicked={onBackToList}
                    >
                        <EventHeadline event={event} />
                    </TextButton>
                    <Avatar
                        className={styles.avatar}
                        userId={userId}
                        size="small"
                    />
                    <IconButton
                        className={styles.close}
                        onClick={onClose}
                        icon="chevron-right"
                    />
                </Heading2>
                <Scrollable>
                    <table>
                        <tbody>
                            {details.map(([label, content], i) => (
                                <tr key={i}>
                                    <td data-tooltip={label}>
                                        <div className={styles.key}>
                                            {label}
                                        </div>
                                    </td>
                                    <td data-testid={label}>
                                        <div className={styles.value}>
                                            {content}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Scrollable>
                <div className={styles.footer}>
                    <div>
                        {exportable && (
                            <IconButton
                                icon="download"
                                onClicked={onExportTelemetry}
                                data-tooltip="Export telemetry"
                            />
                        )}
                        {mutable && (
                            <>
                                <IconButton
                                    className={styles.icons}
                                    icon="edit"
                                    onClicked={onEdit}
                                    data-tooltip="Edit"
                                />
                                <IconButton
                                    className={styles.icons}
                                    icon="delete"
                                    onClicked={onDelete}
                                    data-tooltip="Delete"
                                />
                            </>
                        )}
                    </div>

                    <StandardButton onClick={onBackToList}>
                        View All {`${title}`}
                    </StandardButton>
                </div>
            </div>
        );
    }

    private onDownload = (): void => {
        const { event } = this.props;
        const { time, endTime } = event;
        if (time && endTime) {
            appStore.onDemandData(
                "",
                parseIsoDate(time),
                parseIsoDate(endTime),
            );
        } else if (time) {
            appStore.onDemandData("", parseIsoDate(time));
        }
    };

    private onClose = (event: MouseEvent): void => {
        event.preventDefault();
        observeStore.closeEventSidebar();
    };

    private onBackToList = () => {
        observeStore.showEventList();
    };

    private onEdit = (): void => {
        const { event } = this.props;
        if (event.type === "annotation") {
            observeStore.editAnnotation(event);
        } else if (event.type === "comment") {
            observeStore.editComment(event);
        }
    };

    private onDelete = () => {
        const { event } = this.props;
        if (event.type === "annotation") {
            appStore.setConfirm({
                title: "Delete?",
                message: "Delete annotation?",
                onOk: async () => {
                    await annotationStore.delete(defined(event.id));
                    appStore.setSnackbar("Annotation deleted");
                    this.onBackToList();
                },
            });
        } else if (event.type === "comment") {
            appStore.setConfirm({
                title: "Delete?",
                message: "Delete comment?",
                onOk: async () => {
                    await commentStore.delete(defined(event.id));
                    appStore.setSnackbar("Comment deleted");
                    this.onBackToList();
                },
            });
        }
    };

    private onExportTelemetry = () => {
        const { event, filter, context } = this.props;
        const { time, endTime, streamName, streamType } = event;
        const [start, end] = observeStore.range;
        const query = {
            ...combineFilters(context, filter),
            start: start.toISOString(),
            end: end.toISOString(),
        };
        observeStore.exportTelemetry(
            {
                ...query,
                names: (streamName && [streamName]) || query.names,
                types:
                    ((streamType && [streamType]) as StreamType[]) ||
                    query.types,
            },
            parseIsoDate(time),
            new Date(defined(endTime || undefined)),
        );
    };
}
